import type { GetWebcastViewerNewAuthQuery } from '../generated/graphql-viewer';

import { devLog } from './dev-log';
import { getViewerOperationName } from './get-viewer-operation-name';
import { isLastModifiedNewer } from './is-last-modified-newer';
import { getSessionStorageItem, setSessionStorageItem } from './session-storage';

/**
 * Custom fetch function to handle Last-Modified header logic in the Viewer
 * @param url - The URL or RequestInfo to fetch
 * @param requestInit - Optional RequestInit object for fetch configuration
 * @returns A Promise that resolves to the Response object
 * @throws DOMException if the Last-Modified header is not newer than the previous one
 */
export async function viewerFetch(url: URL | RequestInfo, requestInit?: RequestInit) {
  const operationName = getViewerOperationName(requestInit);
  const response = await fetch(url, requestInit);
  const lastModified = response.headers.get('Last-Modified');

  // Do not block responses during the authentication check.
  // We make multiple GetWebcastViewerNewAuth calls and need to receive all responses.
  if (operationName === 'GetWebcastViewerNewAuth') {
    try {
      const { data } = (await response.clone().json()) as { data: GetWebcastViewerNewAuthQuery };
      const accessGranted = data.webcastNewAuth?.authenticationWorkflow.accessGranted;

      if (accessGranted !== true) return response;
    } catch {
      // If JSON parsing fails, return the response without blocking
      return response;
    }
  }

  if (lastModified) {
    const storageKey = operationName ? `Old-Last-Modified:${operationName}` : 'Old-Last-Modified';
    const oldLastModified = getSessionStorageItem(storageKey);
    const isNewer = isLastModifiedNewer(lastModified, oldLastModified);

    if (!oldLastModified || isNewer) setSessionStorageItem(storageKey, lastModified);
    else {
      devLog('💤 Stale Last-Modified detected:', { operationName, lastModified, oldLastModified });
      throw new DOMException(
        `Last-Modified header from ${operationName || 'Viewer'} (${lastModified}) is not newer than previous (${oldLastModified})`,
        'ResponseNotNewer'
      );
    }
  }

  return response;
}
